import {merge} from '#core/utils/config-utils.js';
import {resolveBackground} from '#core/utils/color-utils.js';

import {
    generateEventCardBadge,
    generateEventCardBadgeRow,
} from '#core/application/modules/tiles/event-card-badge/index.js';
import {generateEventTimingStatus} from '#core/application/modules/tiles/event-timing-status/index.js';
import {generatePriceBoostBanner} from '#core/application/modules/tiles/price-boost-banner/index.js';
import {
    generateAvantgardeEventCardInplayScoreAccent,
    generateAvantgardeOddsButton,
} from '#core/application/modules/cards-and-promotions/avantgarde-cards/avantgarde-event-card-common.js';

const TRANSPARENT_ODDS_CONTAINER = {
    ...resolveBackground('none'),
    borderWidth: 0,
    borderRadius: 0,
};

function generateSelectionEventCardAvantgardeDetails(PALETTE, CARD_DETAILS) {
    const CARD_TIMING_STATUS = generateEventTimingStatus(CARD_DETAILS.TIMING_STATUS);

    return {
        sportCountryIcons: {
            sportIcon: {
                icon: {
                    style: {
                        fill: CARD_DETAILS.SPORT_ICON_COLOR,
                        minWidth: 12,
                    },
                },
            },
            countryIcon: {
                icon: {
                    style: {
                        fill: CARD_DETAILS.SPORT_ICON_COLOR,
                        minWidth: 12,
                    },
                },
            },
        },
        genericEventCard: {
            container: {
                style: {
                    borderColor: PALETTE.PROMOTION_ACCENT_BORDER,
                },
            },
            title: {
                style: CARD_DETAILS.EVENT_NAME_TEXT,
            },
        },
        boostedEventCard: {
            container: {
                style: {
                    borderColor: PALETTE.BOOST_ACCENT_BORDER,
                },
            },
            title: {
                style: CARD_DETAILS.EVENT_NAME_TEXT,
            },
            clickableArea: {
                style: {
                    base: {
                        padding: '0 0 8px',
                    },
                },
            },
        },
        headerTime: CARD_TIMING_STATUS,
        scoreboard: generateAvantgardeEventCardInplayScoreAccent(CARD_DETAILS),
        selectionName: {
            default: {
                style: {
                    lineHeight: 1.3,
                    ...CARD_DETAILS.SELECTION_NAME_TEXT,
                },
            },
            withPromotionText: {
                style: {
                    lineHeight: 1.3,
                    ...CARD_DETAILS.SELECTION_NAME_TEXT,
                },
            },
        },
        marketName: {
            default: {
                style: {
                    lineHeight: 1.3,
                    ...CARD_DETAILS.SELECTION_MARKET_TEXT,
                },
            },
            withPromotionText: {
                style: {
                    lineHeight: 1.3,
                    ...CARD_DETAILS.SELECTION_MARKET_TEXT,
                },
            },
        },
        promotionText: {
            style: {
                lineHeight: 1.3,
                ...CARD_DETAILS.SELECTION_PROMOTION_TEXT,
            },
        },
        selectionButton: generateAvantgardeOddsButton(CARD_DETAILS.ODDS_BUTTON),
    };
}

export function avantgardeSelectionCardsFactory(PALETTE, COLOR_MODE) {
    return {
        SelectionCardAvantgarde: {
            mode: COLOR_MODE,
            showBackgroundSportImage: !PALETTE.PARENT.CARD_WATERMARK_ICON_HIDDEN,
            showCustomBackgroundImage: true,
            competitorIconOptions: {
                icons: {
                    default: 'generic/team-kit-default.svg',
                },
            },
            badgeRow: generateEventCardBadgeRow(PALETTE.PROMOTION_EVENT_CARD_BADGE),
            boostedBadgeRow: generateEventCardBadgeRow(PALETTE.BOOST_EVENT_CARD_BADGE),
            categoryBadge: generateEventCardBadge(PALETTE.PROMOTION_EVENT_CARD_BADGE),
            headerContainer: {
                style: {
                    padding: 8,
                },
            },
            cardBodyContainer: {
                createStackingContext: true,
            },
            boostedCategoryBadge: generateEventCardBadge(PALETTE.BOOST_EVENT_CARD_BADGE),
            priceBoostBanner: merge(generatePriceBoostBanner(PALETTE.CARD_SELECTION_PRICE_BOOST_BANNER), {
                priceBoostContainer: {
                    itemSpacing: '8px',
                    style: {
                        borderRadius: '10px',
                        minHeight: '44px',
                        justifyContent: 'center',
                        alignItems: 'center',
                    },
                },
                priceBoostTitleAndImageContainer: {
                    itemSpacing: '4px',
                    style: {
                        alignItems: 'center',
                        flexGrow: 0,
                        flexShrink: 0,
                    },
                },
                priceBoostTitle: {
                    container: {
                        display: 'none',
                    },
                },
                basePriceContainer: {
                    itemSpacing: '6px',
                    style: {
                        alignItems: 'center',
                        flexGrow: 0,
                        flexShrink: 0,
                        justifyContent: 'center',
                    },
                },
                basePriceTitle: {
                    container: {
                        display: 'none',
                    },
                },
                basePriceAmount: {
                    style: {
                        textDecoration: 'line-through',
                    },
                },
                basePriceImage: {
                    style: {
                        minWidth: '9px',
                        minHeight: '9px',
                        width: '9px',
                        height: '9px',
                    },
                },
                priceBoostImage: {
                    style: {
                        minWidth: '22px',
                        minHeight: '22px',
                    },
                },
                selectionButton: {
                    view: {
                        width: 'auto',
                        height: '28px',
                        handicapDisplay: false,
                        nameDisplay: 'hide',
                        _mobile_nameDisplay: 'hide',
                        container: {
                            borderWidth: 0,
                            borderRadius: 0,
                        },
                        containerInactive: TRANSPARENT_ODDS_CONTAINER,
                        containerInactivehover: TRANSPARENT_ODDS_CONTAINER,
                        containerActive: TRANSPARENT_ODDS_CONTAINER,
                        containerActivehover: TRANSPARENT_ODDS_CONTAINER,
                        containerDisabled: TRANSPARENT_ODDS_CONTAINER,
                    },
                },
            }),
            eventCardDetails: generateSelectionEventCardAvantgardeDetails(PALETTE, PALETTE.CARD_EVENT_DETAILS_LIGHT),
            eventCardDetailsDark: generateSelectionEventCardAvantgardeDetails(PALETTE, PALETTE.CARD_EVENT_DETAILS_DARK),
        },
    };
}
